import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TextField } from "@mui/material";
import { FormHelperText, FormControl, Grid, Typography } from "@financeable-com-au/financeable-ui";
import * as Yup from "yup";
import { debounce } from "lodash";
import { employmentTypeList } from "@/constants";
import { useGetLocalAndLenderErrors, useShowRequiredFields } from "@/hooks/useRequiredFields";
import {
  saveEmployer,
  setRequiredFieldsErrors,
  userDetailsSelector,
  // @ts-expect-error: ignoring as it is existing js file
} from "@/store/slices/applicationFormSlice";
import type { ApplicationFormSlice, Customer, UserDetailsState } from "@/store/slices/types/applicationFormSlice";
// @ts-expect-error: ignoring as it is existing js file
import RadioButtonGroup from "../Utils/RadioButtonGroup";
// @ts-expect-error: ignoring as it is existing js file
import regex from "../../utils/regex";

interface CustomerEmployer {
  _id: string;
  id?: string;
  employmentType?: string;
  timeOfEmployment?: string | number;
}

interface EmploymentDetailsCondensedProps {
  customer: Customer;
  employer: CustomerEmployer;
  index: number;
  applicationId: string;
  fieldErrors: Record<string, string>[];
  setFieldErrors: (errors: Record<string, string>[] | Record<string, string>) => void;
  isDisableEdit: boolean;
  isCustomerLogin: boolean;
}

const timeOfEmploymentValidationSchema = Yup.object({
  timeOfEmployment: Yup.string()
    .optional()
    .matches(regex.timeOfEmploymentRegex, {
      message: "Employment length must be up to 1200 months",
    })
    .max(4, "Maximum of 4 digits"),
});

const getFieldErrors = (fieldErrors: Record<string, string>[]) =>
  Array.isArray(fieldErrors) ? (fieldErrors[0] ?? {}) : (fieldErrors ?? {});

const EmploymentDetailsCondensed = React.memo(
  ({
    customer,
    employer,
    index,
    applicationId,
    fieldErrors,
    setFieldErrors,
    isDisableEdit,
    isCustomerLogin,
  }: EmploymentDetailsCondensedProps) => {
    const dispatch = useDispatch();
    const { requireFieldErrors } = useSelector<ApplicationFormSlice, UserDetailsState>(userDetailsSelector);
    const { resetInvalidRequiredFieldsByFieldName } = useShowRequiredFields();
    const { getCustomerOrEntityLocalAndLenderError } = useGetLocalAndLenderErrors();
    const isDisable = isCustomerLogin ? false : isDisableEdit;
    const [allValues, setAllValues] = useState({
      employmentTypeValue: employer?.employmentType ?? "",
      timeOfEmployment:
        employer?.timeOfEmployment === undefined || employer?.timeOfEmployment === null
          ? ""
          : String(employer.timeOfEmployment),
    });

    useEffect(() => {
      setAllValues({
        employmentTypeValue: employer?.employmentType ?? "",
        timeOfEmployment:
          employer?.timeOfEmployment === undefined || employer?.timeOfEmployment === null
            ? ""
            : String(employer.timeOfEmployment),
      });
    }, [employer]);

    const setLocalFieldError = useCallback(
      (fieldName: string, message: string) => {
        setFieldErrors({
          ...getFieldErrors(fieldErrors),
          [fieldName]: message,
        });
      },
      [fieldErrors, setFieldErrors],
    );

    const debouncedValidation = useMemo(
      () =>
        debounce(async (fieldName: string, value: string) => {
          const name = fieldName.split("_")[0];
          try {
            await timeOfEmploymentValidationSchema.validateAt(name, { [name]: value });
            setLocalFieldError(name, "");
            dispatch(
              setRequiredFieldsErrors({
                ...requireFieldErrors,
                [fieldName]: "",
              }),
            );
          } catch (error) {
            const message = error instanceof Error ? error.message : "";
            setLocalFieldError(name, message);
            dispatch(
              setRequiredFieldsErrors({
                ...requireFieldErrors,
                [fieldName]: message,
              }),
            );
          }
        }, 300),
      [dispatch, requireFieldErrors, setLocalFieldError],
    );

    useEffect(() => {
      return () => {
        debouncedValidation.cancel();
      };
    }, [debouncedValidation]);

    const handleChangeTimeOfEmployment = (value: string, fieldName: string) => {
      const name = fieldName.split("_")[0];
      setAllValues((currentValues) => ({ ...currentValues, [name]: value }));
      debouncedValidation(fieldName, value);
    };

    const handleChangeEmploymentType = (value: string) => {
      resetInvalidRequiredFieldsByFieldName({
        section: "customer",
        fieldName: "employmentType",
      });
      setAllValues((currentValues) => ({ ...currentValues, employmentTypeValue: value }));
      dispatch(
        saveEmployer({
          data: {
            employmentType: value,
            employerId: employer._id,
            customer: customer._id,
            applicationId,
          },
        }),
      );
    };

    const handleSaveTimeOfEmployment = (value: string) => {
      const numericValue = Number(value);
      if (numericValue > 0 && numericValue <= 1200) {
        dispatch(
          saveEmployer({
            data: {
              timeOfEmployment: numericValue,
              employerId: employer._id,
              customer: customer._id,
              applicationId,
            },
          }),
        );
      }
    };

    const getLocalAndLenderError = ({
      fieldName,
      localFieldName,
    }: {
      fieldName: string;
      localFieldName: "employmentTypeValue" | "timeOfEmployment";
    }) =>
      getCustomerOrEntityLocalAndLenderError({
        fieldError:
          getFieldErrors(fieldErrors)[localFieldName === "employmentTypeValue" ? "employmentType" : localFieldName],
        localState: allValues,
        fieldName,
        localFieldName,
      });

    const employmentTypeFieldName = `employers.[0].employmentType_${customer._id}_${index}`;
    const employmentTypeError = getLocalAndLenderError({
      fieldName: employmentTypeFieldName,
      localFieldName: "employmentTypeValue",
    });
    const timeOfEmploymentFieldName = `timeOfEmployment_${customer._id}_${index}`;
    const timeOfEmploymentError = getLocalAndLenderError({
      fieldName: `employers.[0].${timeOfEmploymentFieldName}`,
      localFieldName: "timeOfEmployment",
    });

    return (
      <Grid container spacing={4} alignItems="end">
        <Grid sm={12} md={12}>
          <FormControl error={employmentTypeError}>
            <Typography level="body-sm" bold component="h6">
              Employment type
            </Typography>
            <RadioButtonGroup
              disabled={isDisable}
              value={allValues.employmentTypeValue}
              handleValue={handleChangeEmploymentType}
              optionStyle={{
                width: "80%",
                padding: "8px",
                minHeight: "48px",
                ...(employmentTypeError && { borderColor: "#d32f2f" }),
              }}
              name={`employmentType_${customer._id}_${index}`}
              options={employmentTypeList}
            />
            {employmentTypeError && <FormHelperText>{employmentTypeError}</FormHelperText>}
          </FormControl>
        </Grid>

        <Grid sm={12} md={6}>
          <Typography level="body-xs" mb={2}>Time of employment (in months)</Typography>
          <TextField
            fullWidth
            id={`timeOfEmployment_${customer._id}_${index}`}
            label="Time of employment"
            variant="filled"
            type="text"
            size="small"
            name={timeOfEmploymentFieldName}
            value={allValues.timeOfEmployment}
            disabled={isDisable}
            error={Boolean(timeOfEmploymentError)}
            helperText={timeOfEmploymentError || undefined}
            onChange={(event) => handleChangeTimeOfEmployment(event.target.value, timeOfEmploymentFieldName)}
            onBlur={(event) => handleSaveTimeOfEmployment(event.target.value)}
          />
        </Grid>
      </Grid>
    );
  },
);

export { EmploymentDetailsCondensed };
export type { CustomerEmployer };
